import { LogOut, MenuIcon } from 'lucide-react'
import { Button } from '@components/common/button'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LOGIN_PAGE_ROUTE,
  PROFILE_PAGE_ROUTE,
  SETTINGS_PAGE_ROUTE,
} from '@pma/utils/page-routes'
import { destroyCookie, setCookie } from 'nookies'
import { ACCESS_TOKEN, LOCALE, navOptions } from '@pma/utils/constants'

import { parseCookies } from 'nookies'
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarShortcut,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from '@components/common/menu-bar'
import { DictType, Locale } from '@pma/types'
import { cn } from '@pma/utils/helpers'
import { Separator } from '@components/common/separator'
import { locales } from '@pma/locale/dictionaries'
import { useModal } from '@stores/modal-store'
import { Fragment } from 'react'
import axios from 'axios'
import { SERVER_URL, USER_LANGUAGE_ENDPOINT } from '@pma/utils/api-routes'
import { useMutation } from '@tanstack/react-query'
import { toast } from '@pma/hooks/use-toast'
import { useAuth } from '@pma/stores/auth-store'
import useTranslator from '@pma/hooks/use-translator'

const MobileNavMenu = () => {
  const { signIn, signOut } = useAuth()
  const { translate, } = useTranslator()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const modal = useModal()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const location = useLocation()
  const pathname = location.pathname
  const activeTab = pathname

  const changeLocale = (locale: Locale) => {
    i18n.changeLanguage(locale as string)

    setCookie(null, LOCALE, locale, {
      maxAge: 1000 * 60 * 60 * 24 * 360,
      sameSite: 'strict',
      path: '/',
    })
    changeUserLanguageMutation.mutate(locale)
  }
  const changeUserLanguage = async (locale: Locale) => {
    const response = await axios.post(
      SERVER_URL + USER_LANGUAGE_ENDPOINT,
      {
        selected_language: locale,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const changeUserLanguageMutation = useMutation({
    mutationKey: ['user-language'],
    mutationFn: changeUserLanguage,

    onSuccess: (data) => {
      signIn({ ...data })

      toast({
        title: translate('Success'),
        description: `${translate('Default language')} ${translate('updated')}`,
        variant: 'success',
      })
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate('Error'),
        description: translate(errorResponse || 'Unable to update default language'),
        variant: 'error',
      })
    },
  })

  const logOut = () => {
    signOut()
    destroyCookie(undefined, ACCESS_TOKEN)
    document.cookie = ACCESS_TOKEN + '=; Max-Age=0'
    navigate(LOGIN_PAGE_ROUTE)
  }

  if (!accessToken) {
    return (
      <div className='hidden md:block'>
        <Link to={LOGIN_PAGE_ROUTE}>
          <Button variant={'secondary'} size={'default'} className={`w-[7rem]`}>
            {t('Sign In')}
          </Button>
        </Link>
      </div>
    )
  }
  return (
    <div className='z-50 items-center'>
      <Menubar className='border-none bg-none p-0 text-black'>
        <MenubarMenu>
          <MenubarTrigger className='flex items-center justify-center border bg-none'>
            <MenuIcon />
          </MenubarTrigger>
          <MenubarContent
            className='border-none w-[100vw] mt-2 rounded-none bg-background'
            style={{
              background: 'linear-gradient(180deg, #343cf9, #7f6aff)',
            }}
          >
            <div>
              {navOptions.map(({ label, href, ...rest }: DictType) => (
                <MenubarItem
                  key={`${label}-${href}`}
                  className={cn(
                    'flex text-white',
                    `${activeTab}` === href && 'p-2 rounded-md bg-black ',
                  )}
                >
                  <Link
                    to={href}
                    className={'text-muted-primary flex items-center gap-2 '}
                  >
                    <rest.icon className='h-4 w-4' />

                    {translate(label)}
                  </Link>
                </MenubarItem>
              ))}
            </div>

            <Separator className='my-4' />
            <Link to={PROFILE_PAGE_ROUTE}>
              <MenubarItem>{translate('Profile')}</MenubarItem>
            </Link>
            <MenubarSeparator className='mx-1 bg-red-100' />

            <Link to={SETTINGS_PAGE_ROUTE}>
              <MenubarItem>{translate('Settings')}</MenubarItem>
            </Link>
            <MenubarSeparator className='mx-1 bg-red-100' />
            <MenubarItem
              onClick={() => {
                modal.onOpen()
                modal.setContext('workspace')
              }}
            >
              {translate('Switch Workspace')}
            </MenubarItem>
            <MenubarSeparator className='mx-1 bg-red-100' />

            <MenubarSub>
              <MenubarSubTrigger className='w-2/3'>
                {translate('Language')}
              </MenubarSubTrigger>
              <MenubarSubContent className='bg-background' avoidCollisions>
                {locales.map((locale: DictType, index: number) => (
                  <Fragment key={index}>
                    <MenubarItem
                      onClick={() => changeLocale(locale.locale)}
                      className='flex items-center justify-between w-full '
                    >
                      <span>{translate(locale.name)}</span>
                      <span>{locale.emoji}</span>{' '}
                    </MenubarItem>
                    {index < locales.length - 1 && <MenubarSeparator />}
                  </Fragment>
                ))}
              </MenubarSubContent>
            </MenubarSub>

            <MenubarSeparator className='mx-1 bg-red-100' />
            <MenubarItem className='text-red-500' onClick={() => logOut()}>
              {translate('Logout')}
              <MenubarShortcut>
                <LogOut size={16} className='text-red-500' />
              </MenubarShortcut>
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>
      </Menubar>
    </div>
  )
}

export default MobileNavMenu
